import { useState } from "react";
import { Check, RefreshCw, Sparkles } from "lucide-react";

const questions = [
  {
    id: "tib",
    q: "How long has your business been operating?",
    options: [
      { label: "Less than 6 months", value: 1 },
      { label: "6 – 12 months", value: 2 },
      { label: "1 – 2 years", value: 3 },
      { label: "2+ years", value: 4 },
    ],
  },
  {
    id: "rev",
    q: "What is your average monthly revenue?",
    options: [
      { label: "Under $10K", value: 1 },
      { label: "$10K – $25K", value: 2 },
      { label: "$25K – $75K", value: 3 },
      { label: "$75K+", value: 4 },
    ],
  },
  {
    id: "credit",
    q: "Roughly, how is your personal credit?",
    options: [
      { label: "Below 550", value: 1 },
      { label: "550 – 619", value: 2 },
      { label: "620 – 699", value: 3 },
      { label: "700+", value: 4 },
    ],
  },
];

export default function ApprovalQuiz() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);

  const handlePick = (val) => {
    const next = { ...answers, [questions[step].id]: val };
    setAnswers(next);
    if (step + 1 < questions.length) setStep(step + 1);
    else setDone(true);
  };

  const score = Object.values(answers).reduce((a, b) => a + b, 0);
  const max = questions.length * 4;
  const probability = Math.round((score / max) * 100);
  const tier =
    probability >= 80 ? { label: "Excellent", color: "text-emerald-400", note: "Pre-qualified for our best programs." } :
    probability >= 60 ? { label: "Strong",    color: "text-gold",         note: "Multiple funding options available — including same-day." } :
    probability >= 40 ? { label: "Moderate",  color: "text-amber-400",    note: "Likely qualified for revenue-based options." } :
                        { label: "Possible",  color: "text-orange-400",   note: "We work with bank turn-downs — let's talk." };

  const reset = () => { setAnswers({}); setStep(0); setDone(false); };

  return (
    <section id="quiz" data-testid="quiz-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <div className="text-xs tracking-[0.25em] uppercase font-bold text-gold mb-5">Approval Probability</div>
          <h2 className="font-heading text-4xl md:text-5xl font-light text-white tracking-tighter">
            How likely are you to get funded? <span className="text-gold">3 questions.</span>
          </h2>
          <p className="mt-6 text-white/60 text-lg leading-relaxed">
            Soft check only. No credit pull. No commitment. Just a fast read on which of our
            programs fits you best — so you don't waste a minute.
          </p>
        </div>

        <div className="lg:col-span-7 bg-[#0A1326] border border-white/10 p-8 md:p-12 min-h-[420px]">
          {!done ? (
            <>
              <div className="flex items-center justify-between mb-8">
                <div className="text-xs tracking-[0.2em] uppercase text-white/50">
                  Question {step + 1} of {questions.length}
                </div>
                <div className="flex gap-1">
                  {questions.map((_, i) => (
                    <span key={i} className={`h-1 w-10 ${i <= step ? "bg-gold" : "bg-white/10"}`} />
                  ))}
                </div>
              </div>

              <h3 className="font-heading text-2xl md:text-3xl text-white mb-8" data-testid="quiz-question">
                {questions[step].q}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {questions[step].options.map((o) => (
                  <button
                    key={o.label}
                    data-testid={`quiz-option-${step}-${o.value}`}
                    onClick={() => handlePick(o.value)}
                    className="text-left border border-white/15 hover:border-gold hover:bg-gold/5 px-5 py-4 text-white transition-all group"
                  >
                    <span className="flex items-center justify-between">
                      <span>{o.label}</span>
                      <span className="opacity-0 group-hover:opacity-100 text-gold transition-opacity">→</span>
                    </span>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div data-testid="quiz-result">
              <Sparkles className="w-8 h-8 text-gold mb-6" />
              <div className="text-xs tracking-[0.2em] uppercase text-white/50">Your Approval Score</div>
              <div className="mt-3 flex items-baseline gap-4">
                <div className="font-heading text-7xl font-light text-gold tracking-tighter" data-testid="quiz-score">
                  {probability}<span className="text-3xl">%</span>
                </div>
                <div className={`font-heading text-2xl ${tier.color}`}>{tier.label}</div>
              </div>
              <p className="mt-6 text-white/70 text-lg max-w-lg">{tier.note}</p>

              <div className="mt-10 flex flex-wrap gap-3">
                <a
                  href="#apply"
                  data-testid="quiz-cta"
                  className="inline-flex items-center gap-2 bg-gold hover:bg-gold-400 text-navy-950 font-semibold px-7 py-3.5 transition-all hover:-translate-y-0.5"
                >
                  <Check className="w-4 h-4" /> Continue to Application
                </a>
                <button
                  onClick={reset}
                  data-testid="quiz-reset"
                  className="inline-flex items-center gap-2 border border-white/20 hover:border-white/60 text-white px-7 py-3.5 transition-all"
                >
                  <RefreshCw className="w-4 h-4" /> Retake quiz
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
